/**
 * SS-4211 — start a NEW support thread with a practice from the console.
 *
 * The API (`POST /support/inbox/outbound`) opens one thread per recipient, grouped, and
 * sends it by email, in the widget, or both. This is its composer: which practice, who
 * there — their one contact, everyone who can change settings, everyone holding one of the
 * practice's OWN roles (standard or custom, by the name the practice gave it), or people
 * picked by name; all of it from `GET /support/inbox/recipients`, so nothing here guesses a
 * role — which channels, a subject and a message under the same rules as a reply — the
 * length cap and the attachment policy.
 *
 * One `clientMessageId` per composed message, kept across retries, so a send whose answer
 * was lost is replayed by the server rather than opening a second group of threads. It is
 * renewed only once a send has succeeded or the message itself changes.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
  Spinner,
  Stack,
  Text,
  Textarea,
  useToast,
} from "@chakra-ui/react";
import AsyncSelect from "react-select/async";
import debounce from "lodash.debounce";
import { cqAPI, loadCustomers } from "../../lib/Auth";
import {
  ALLOWED_ATTACHMENT_TYPES,
  ATTACHMENT_ACCEPT,
  MAX_ATTACHMENTS,
  MAX_ATTACHMENT_BYTES,
  MAX_ATTACHMENT_MB,
  attachmentPayload,
  newClientMessageId,
  uploadAttachment,
} from "./supportAttachmentPolicy";

// Matches MAX_MESSAGE_CHARS in controllers/support.js, which refuses anything longer.
export const MAX_MESSAGE_CHARS = 20000;
// Matches the SupportTicket subject cap.
export const MAX_SUBJECT_CHARS = 200;
const MAX_STAFF_NOTE_CHARS = 2000;
const WRITE_TIMEOUT_MS = 120000;

export const RECIPIENT_MODES = [
  { value: "contact", label: "Practice contact" },
  { value: "settings", label: "Everyone who can change settings" },
  { value: "role", label: "By role" },
  { value: "pick", label: "Pick people" },
];

// The permission behind the "change settings" shortcut, by the name the API reads.
export const SETTINGS_PERMISSION = "BUSINESS_SETTINGS";

export const CHANNEL_CHOICES = [
  { value: "both", label: "Both", channels: ["email", "widget"] },
  { value: "email", label: "Email", channels: ["email"] },
  { value: "widget", label: "Widget", channels: ["widget"] },
];

/**
 * The `recipients` the API is sent for a choice — `{}` means "the practice contact".
 * `picked` holds person ids, `pickedRoles` role NAMES as the practice defines them.
 */
export const recipientsFor = (mode, picked, pickedRoles = new Set()) => {
  if (mode === "settings") return { permissions: [SETTINGS_PERMISSION] };
  if (mode === "role") return { roles: [...pickedRoles] };
  if (mode === "pick") return { uids: [...picked] };
  return {};
};

const attachmentRejection = (file, room) => {
  const name = file.name || "That file";
  if (room <= 0) return `You can attach up to ${MAX_ATTACHMENTS} files to a message.`;
  if (!ALLOWED_ATTACHMENT_TYPES.includes(String(file.type || "").toLowerCase())) {
    return `${name} is not a file type that can be attached.`;
  }
  if (file.size > MAX_ATTACHMENT_BYTES) return `${name} is larger than ${MAX_ATTACHMENT_MB}MB.`;
  return "";
};

const personLabel = (person) =>
  [person.name || person.email, person.name && person.email ? `<${person.email}>` : ""]
    .filter(Boolean)
    .join(" ");

function SupportOutboundComposer({ isOpen, onClose, onSent }) {
  const toast = useToast();
  const [practice, setPractice] = useState(null);
  const [directory, setDirectory] = useState(null);
  const [directoryError, setDirectoryError] = useState("");
  const [loadingDirectory, setLoadingDirectory] = useState(false);
  // Bumped by "Retry" after a failed load: the same practice, asked again, draft kept.
  const [directoryAttempt, setDirectoryAttempt] = useState(0);
  const [mode, setMode] = useState("contact");
  const [picked, setPicked] = useState(() => new Set());
  const [pickedRoles, setPickedRoles] = useState(() => new Set());
  const [channel, setChannel] = useState("both");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [staffNote, setStaffNote] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const fileInputRef = useRef(null);
  // The idempotency token of what is composed right now — see the note at the top.
  const clientIdRef = useRef(newClientMessageId());
  // Which directory request is the live one. Compared by REQUEST, not by practice: going
  // A → B → A leaves two requests for A in flight, and the first must not overwrite the
  // answer to the second.
  const directoryRequest = useRef(0);
  // The practice files are being uploaded for. An upload finishing after the practice
  // changed belongs to the OLD one — the API refuses a file from another practice — so it
  // is dropped rather than attached.
  const bidRef = useRef("");
  // A send can outlive the composer — browser Back unmounts it mid-request — and its
  // answer navigates. Landing after the agent has gone elsewhere must not move them.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const bid = practice?._id ? String(practice._id) : "";
  useEffect(() => { bidRef.current = bid; }, [bid]);

  const reset = useCallback(() => {
    setPractice(null);
    setDirectory(null);
    setDirectoryError("");
    setMode("contact");
    setPicked(new Set());
    setPickedRoles(new Set());
    setChannel("both");
    setSubject("");
    setMessage("");
    setStaffNote("");
    setAttachments([]);
    clientIdRef.current = newClientMessageId();
  }, []);

  // A different message is a different send.
  const renewSendId = () => { clientIdRef.current = newClientMessageId(); };

  useEffect(() => {
    if (!bid) return;
    directoryRequest.current += 1;
    const request = directoryRequest.current;
    const live = () => directoryRequest.current === request;
    setLoadingDirectory(true);
    setDirectory(null);
    setDirectoryError("");
    setPicked(new Set());
    setPickedRoles(new Set());
    cqAPI
      .get(`/support/inbox/recipients?bid=${encodeURIComponent(bid)}`)
      .then((response) => {
        if (!live()) return;
        if (response?.type !== "ok") {
          setDirectoryError(response?.msg || "Could not load the practice's staff.");
          return;
        }
        setDirectory(response.data || { staff: [] });
      })
      .catch(() => {
        if (live()) setDirectoryError("Could not load the practice's staff.");
      })
      .finally(() => {
        if (live()) setLoadingDirectory(false);
      });
  }, [bid, directoryAttempt]);

  const loadOptions = useMemo(
    () => debounce((inputValue, callback) => {
      loadCustomers(inputValue).then((options) => callback(options || []));
    }, 500),
    []
  );

  const staff = directory?.staff || [];
  const practiceRoles = directory?.roles || [];
  const contact = directory?.default_contact || null;
  const personById = (id) => staff.find((person) => String(person._id) === String(id));
  const settingsPeople = (directory?.can_change_settings || []).map(personById).filter(Boolean);
  // Everyone holding a checked role, once each — a person with two of them is one person.
  const rolePeople = [...new Set(practiceRoles
    .filter((role) => pickedRoles.has(role.name))
    .flatMap((role) => (role.members || []).map(String)))]
    .map(personById)
    .filter(Boolean);

  const recipientCount = mode === "contact" ? (contact ? 1 : 0)
    : mode === "settings" ? settingsPeople.length
      : mode === "role" ? rolePeople.length
        : picked.size;

  const toggleIn = (setter) => (key) => {
    renewSendId();
    setter((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const togglePicked = toggleIn(setPicked);
  const toggleRole = toggleIn(setPickedRoles);

  // Mirrors `attachments`, so two selections made while uploads are running both see
  // the room the other has already taken.
  const attachmentsLiveRef = useRef([]);
  useEffect(() => { attachmentsLiveRef.current = attachments; }, [attachments]);

  const addFiles = async (fileList) => {
    const forBid = bid;
    // The whole batch is judged and RESERVED before any upload starts, the way the reply
    // composer does it: capacity taken per file across the awaits let a second selection
    // spend the same room and carry the message past the cap.
    let room = MAX_ATTACHMENTS - attachmentsLiveRef.current.length;
    const queued = [];
    for (const file of [...(fileList || [])]) {
      const problem = attachmentRejection(file, room);
      if (problem) {
        toast({ title: problem, status: "warning", duration: 4000, isClosable: true });
        continue;
      }
      room -= 1;
      queued.push({
        file,
        entry: { _id: newClientMessageId(), name: file.name, size: file.size, content_type: file.type, uploading: true },
      });
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (!queued.length) return;
    renewSendId();
    attachmentsLiveRef.current = [...attachmentsLiveRef.current, ...queued.map((q) => q.entry)];
    setAttachments(attachmentsLiveRef.current);

    for (const { file, entry } of queued) {
      try {
        const response = await uploadAttachment(file, forBid);
        if (response?.type !== "ok" || !response.url) throw new Error(response?.msg || "upload failed");
        if (bidRef.current !== forBid) continue;
        setAttachments((current) =>
          current.map((a) => (a._id === entry._id ? { ...a, uploading: false, url: response.url } : a))
        );
      } catch (error) {
        console.error("Error uploading an outbound support attachment:", error);
        setAttachments((current) => current.filter((a) => a._id !== entry._id));
        toast({ title: "That file could not be uploaded. Please try again.", status: "error", duration: 4000, isClosable: true });
      }
    }
  };

  const ready = attachments.filter((a) => a.url && !a.uploading);
  const uploading = attachments.length !== ready.length;
  const trimmedSubject = subject.trim();
  const canSend = !!bid && !!trimmedSubject && (!!message.trim() || ready.length > 0)
    && !uploading && recipientCount > 0 && !sending;

  const send = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      const response = await cqAPI.post(
        "/support/inbox/outbound",
        {
          bid,
          recipients: recipientsFor(mode, picked, pickedRoles),
          subject: trimmedSubject,
          message: message.trim(),
          channels: CHANNEL_CHOICES.find((c) => c.value === channel).channels,
          clientMessageId: clientIdRef.current,
          attachments: ready.map(attachmentPayload),
          ...(staffNote.trim() ? { staffNote: staffNote.trim() } : {}),
        },
        WRITE_TIMEOUT_MS
      );
      if (!mountedRef.current) return;
      if (response?.type !== "ok") {
        // Refused, or not sure — the token is KEPT, so trying again replays rather than
        // sending twice.
        toast({ title: response?.msg || "Could not send that message.", status: "error", duration: 5000, isClosable: true });
        return;
      }
      const tickets = response.data?.tickets || [];
      // The threads exist either way — a second send would only replay this one — so the
      // draft is cleared. What is said about it comes from the SEND's own status, which
      // knows about every channel: an email-only send to a partner with no sending
      // domain opened its thread and reached nobody.
      const reachedEveryone = response.data?.send?.status === "sent";
      const retrying = tickets.filter((t) => t.delivery && t.delivery.email === "failed").length;
      const reasons = [...new Set(tickets.map((t) => t.delivery && t.delivery.error).filter(Boolean))];
      toast({
        title: reachedEveryone
          ? `Sent to ${tickets.length} ${tickets.length === 1 ? "person" : "people"}`
          : `Opened ${tickets.length} ${tickets.length === 1 ? "thread" : "threads"}, but not everyone could be reached`,
        description: reachedEveryone ? undefined : [
          retrying ? `${retrying} email${retrying === 1 ? "" : "s"} could not be sent yet — retrying.` : "",
          ...reasons,
        ].filter(Boolean).join(" ") || undefined,
        status: reachedEveryone ? "success" : "warning",
        duration: reachedEveryone ? 4000 : 8000,
        isClosable: true,
      });
      const sentTo = practice;
      reset();
      if (onSent) onSent(response.data, sentTo);
    } catch (error) {
      console.error("Error sending an outbound support message:", error);
      if (mountedRef.current) {
        toast({ title: "Could not send that message.", status: "error", duration: 5000, isClosable: true });
      }
    } finally {
      if (mountedRef.current) setSending(false);
    }
  };

  return (
    // Not closable while a send is in flight: its answer navigates, and landing after the
    // agent has moved on to another ticket would take them away from whatever they are
    // writing there.
    <Modal
      isOpen={isOpen}
      onClose={() => { if (!sending) onClose(); }}
      closeOnEsc={!sending}
      closeOnOverlayClick={!sending}
      size="xl"
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent data-testid="support-outbound-composer">
        <ModalHeader>New message</ModalHeader>
        <ModalCloseButton isDisabled={sending} />
        <ModalBody>
          {/* Nothing can be edited while a send is in flight: whatever changed underneath
              it would be cleared with the draft when it lands, without having been sent. */}
          <fieldset disabled={sending} style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}>
            <Stack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Customer</FormLabel>
                <AsyncSelect
                  inputId="support-outbound-customer"
                  loadOptions={loadOptions}
                  value={practice}
                  getOptionValue={(option) => option._id}
                  getOptionLabel={(option) => option.name}
                  placeholder="Search for a practice"
                  isDisabled={sending}
                  onChange={(option) => {
                    renewSendId();
                    // Files are filed under the practice they were uploaded for; the API
                    // refuses them on a send to any other.
                    if (String(option?._id || "") !== bid) setAttachments([]);
                    setPractice(option);
                  }}
                />
              </FormControl>

              <FormControl as="fieldset" isDisabled={!bid}>
                <FormLabel as="legend">Recipients</FormLabel>
                {loadingDirectory && <Spinner size="sm" />}
                {!!directoryError && (
                  <Flex align="center" gap={2}>
                    <Text fontSize="sm" color="red.500">{directoryError}</Text>
                    <Button size="xs" variant="link" isDisabled={sending || loadingDirectory}
                      onClick={() => setDirectoryAttempt((n) => n + 1)}>
                      Retry
                    </Button>
                  </Flex>
                )}
                <RadioGroup value={mode} onChange={(value) => { renewSendId(); setMode(value); }}>
                  <Stack spacing={1}>
                    {RECIPIENT_MODES.map((option) => (
                      <Radio key={option.value} value={option.value}>{option.label}</Radio>
                    ))}
                  </Stack>
                </RadioGroup>
                {bid && directory && mode === "contact" && (
                  <FormHelperText data-testid="support-outbound-contact">
                    {contact ? personLabel(contact) : "This practice has no contact, and nobody who can change settings, with a verified email address."}
                  </FormHelperText>
                )}
                {bid && directory && mode === "settings" && (
                  <FormHelperText data-testid="support-outbound-settings">
                    {settingsPeople.length
                      ? settingsPeople.map(personLabel).join(", ")
                      : "Nobody at this practice who can change settings has a verified email address."}
                  </FormHelperText>
                )}
                {bid && directory && mode === "role" && (
                  <Box mt={2} maxH="160px" overflowY="auto" borderWidth="1px" borderRadius="md" p={2}>
                    {practiceRoles.length === 0 && <Text fontSize="sm">This practice has no roles to pick from.</Text>}
                    {practiceRoles.map((role) => {
                      const members = (role.members || []).length;
                      return (
                        <Checkbox
                          key={role.name}
                          display="flex"
                          isChecked={pickedRoles.has(role.name)}
                          isDisabled={!members}
                          onChange={() => toggleRole(role.name)}
                        >
                          {role.name}
                          {role.custom ? " (custom)" : ""}
                          {` · ${members ? `${members} ${members === 1 ? "person" : "people"}` : "nobody reachable"}`}
                        </Checkbox>
                      );
                    })}
                    {rolePeople.length > 0 && (
                      <FormHelperText data-testid="support-outbound-role-people">
                        {rolePeople.map(personLabel).join(", ")}
                      </FormHelperText>
                    )}
                  </Box>
                )}
                {bid && directory && mode === "pick" && (
                  <Box mt={2} maxH="160px" overflowY="auto" borderWidth="1px" borderRadius="md" p={2}>
                    {staff.length === 0 && <Text fontSize="sm">Nobody at this practice can be sent a message.</Text>}
                    {staff.map((person) => (
                      <Checkbox
                        key={person._id}
                        display="flex"
                        isChecked={picked.has(String(person._id))}
                        onChange={() => togglePicked(String(person._id))}
                      >
                        {personLabel(person)}
                        {person.roles?.length ? ` · ${person.roles.join(", ")}` : ""}
                      </Checkbox>
                    ))}
                  </Box>
                )}
              </FormControl>

              <FormControl as="fieldset">
                <FormLabel as="legend">Send by</FormLabel>
                <RadioGroup value={channel} onChange={(value) => { renewSendId(); setChannel(value); }}>
                  <Flex gap={4}>
                    {CHANNEL_CHOICES.map((option) => (
                      <Radio key={option.value} value={option.value}>{option.label}</Radio>
                    ))}
                  </Flex>
                </RadioGroup>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Subject</FormLabel>
                <Input
                  value={subject}
                  maxLength={MAX_SUBJECT_CHARS}
                  onChange={(e) => { renewSendId(); setSubject(e.target.value.slice(0, MAX_SUBJECT_CHARS)); }}
                  placeholder="What this is about"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Message</FormLabel>
                <Textarea
                  value={message}
                  maxLength={MAX_MESSAGE_CHARS}
                  rows={6}
                  onChange={(e) => { renewSendId(); setMessage(e.target.value.slice(0, MAX_MESSAGE_CHARS)); }}
                  placeholder="Write to the practice"
                />
                <Flex mt={2} gap={2} align="center" wrap="wrap">
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept={ATTACHMENT_ACCEPT}
                    style={{ display: "none" }}
                    onChange={(e) => addFiles(e.target.files)}
                  />
                  <Button
                    size="xs"
                    variant="outline"
                    isDisabled={!bid || attachments.length >= MAX_ATTACHMENTS}
                    onClick={() => fileInputRef.current && fileInputRef.current.click()}
                  >
                    Attach files
                  </Button>
                  {attachments.map((a) => (
                    <Flex key={a._id} align="center" gap={1} fontSize="xs" borderWidth="1px" borderRadius="md" px={2} py={1}>
                      <Text>{a.name}</Text>
                      {a.uploading ? (
                        <Spinner size="xs" />
                      ) : (
                        <Button
                          size="xs"
                          variant="ghost"
                          aria-label={`Remove ${a.name}`}
                          onClick={() => {
                            renewSendId();
                            setAttachments((current) => current.filter((x) => x._id !== a._id));
                          }}
                        >
                          ×
                        </Button>
                      )}
                    </Flex>
                  ))}
                </Flex>
              </FormControl>

              <FormControl>
                <FormLabel>Staff-only note (optional)</FormLabel>
                <Textarea
                  value={staffNote}
                  maxLength={MAX_STAFF_NOTE_CHARS}
                  rows={2}
                  onChange={(e) => { renewSendId(); setStaffNote(e.target.value.slice(0, MAX_STAFF_NOTE_CHARS)); }}
                  placeholder="The exact change, for whoever makes it if the practice says yes. Never shown to them."
                />
              </FormControl>
            </Stack>
          </fieldset>
        </ModalBody>
        <ModalFooter gap={2}>
          <Button variant="ghost" onClick={onClose} isDisabled={sending}>Cancel</Button>
          <Button colorScheme="blue" onClick={send} isLoading={sending} isDisabled={!canSend}>
            {recipientCount > 1 ? `Send to ${recipientCount}` : "Send"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default SupportOutboundComposer;
